import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Cpu, Plus, ShieldCheck, ShieldAlert, Server, Trash2, CheckCircle2, ArrowUp, ArrowDown, Zap, KeyRound } from "lucide-react";

const PRIVACY = {
    no_train: { label: "No training", cls: "bg-emerald-500/12 text-emerald-600 border-emerald-500/30", Icon: ShieldCheck },
    self_host: { label: "Self-hosted", cls: "bg-sky-500/12 text-sky-600 border-sky-500/30", Icon: Server },
    trains: { label: "Trains on data", cls: "bg-amber-500/12 text-amber-600 border-amber-500/30", Icon: ShieldAlert },
};

function PrivacyBadge({ tier }) {
    const p = PRIVACY[tier] || PRIVACY.no_train;
    const Icon = p.Icon;
    return (
        <Badge variant="outline" className={`gap-1 ${p.cls}`}>
            <Icon className="w-3 h-3" /> {p.label}
        </Badge>
    );
}

export default function AIProviders() {
    const [catalog, setCatalog] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [routes, setRoutes] = useState({});
    const [task, setTask] = useState("concierge");
    const [resolution, setResolution] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState({ provider: "groq", label: "", api_key: "", base_url: "", priority: 100, free_daily: 0 });

    const providerMeta = useMemo(() => Object.fromEntries(catalog.map((c) => [c.id, c])), [catalog]);

    const load = async () => {
        try {
            const [p, a, r] = await Promise.all([
                api.get("/ai/providers"),
                api.get("/ai/accounts"),
                api.get("/ai/routes"),
            ]);
            setCatalog(p.data.catalog);
            setTasks(p.data.tasks);
            setAccounts(a.data);
            setRoutes(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load AI providers");
        }
    };
    useEffect(() => { load(); }, []);

    const openAdd = (provider) => {
        const meta = providerMeta[provider] || {};
        setForm({ provider, label: "", api_key: "", base_url: "", priority: 100, free_daily: meta.free_daily || 0 });
        setAddOpen(true);
    };

    const createAccount = async () => {
        try {
            await api.post("/ai/accounts", {
                provider: form.provider, label: form.label || undefined, api_key: form.api_key,
                base_url: form.base_url || null, priority: Number(form.priority), free_daily: Number(form.free_daily),
            });
            toast.success("Account added");
            setAddOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not add account");
        }
    };

    const patchAccount = async (id, patch) => {
        try {
            await api.patch(`/ai/accounts/${id}`, patch);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        }
    };

    const verify = async (id) => {
        try {
            const { data } = await api.post(`/ai/accounts/${id}/verify`, {});
            data.ok ? toast.success(`Verified — ${data.reason}`) : toast.error(data.reason);
            load();
        } catch (e) {
            toast.error("Verify failed");
        }
    };

    const removeAccount = async (id) => {
        try { await api.delete(`/ai/accounts/${id}`); toast.success("Removed"); load(); }
        catch { toast.error("Delete failed"); }
    };

    // --- routing editor ---
    const items = routes[task] || [];
    const setItems = (next) => setRoutes({ ...routes, [task]: next });
    const addRouteItem = () => {
        if (!accounts.length) return toast.error("Add an account first");
        setItems([...items, { account_id: accounts[0].id, model: "" }]);
    };
    const move = (i, dir) => {
        const next = [...items];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        setItems(next);
    };
    const updateItem = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

    const saveRoutes = async () => {
        try {
            await api.put("/ai/routes", { task, items: items.map((it) => ({ account_id: it.account_id, model: it.model || "" })) });
            toast.success(`Routing saved for ${task}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const preview = async () => {
        try { const { data } = await api.get(`/ai/resolve?task=${task}`); setResolution(data); }
        catch { toast.error("Resolve failed"); }
    };
    const simulate = async () => {
        try { const { data } = await api.post(`/ai/simulate?task=${task}`); setResolution(data); load(); }
        catch { toast.error("Simulate failed"); }
    };

    const accModels = (accountId) => {
        const acc = accounts.find((a) => a.id === accountId);
        return acc ? (providerMeta[acc.provider]?.models || []) : [];
    };
    const accLabel = (id) => {
        const a = accounts.find((x) => x.id === id);
        return a ? `${a.label} · ${a.provider}` : "(deleted)";
    };

    return (
        <div className="space-y-6" data-testid="ai-providers-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                        <Cpu className="w-3.5 h-3.5" /> Intelligence
                    </div>
                    <h1 className="text-2xl font-semibold tracking-tight mt-1">AI Providers &amp; Routing</h1>
                    <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
                        Bring your own keys across providers, add multiple accounts each, and set a priority order that
                        automatically fails over when an account hits its free daily limit. Keys are stored server-side and shown masked.
                    </p>
                </div>
            </div>

            {/* Provider catalog */}
            <div>
                <div className="text-sm font-medium mb-2">Supported providers</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ai-provider-catalog">
                    {catalog.map((p) => (
                        <Card key={p.id} className="overflow-hidden">
                            <CardContent className="py-4 space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="font-medium">{p.name}</div>
                                    <PrivacyBadge tier={p.privacy} />
                                </div>
                                <p className="text-xs text-muted-foreground leading-relaxed">{p.note}</p>
                                <div className="flex items-center justify-between pt-1">
                                    <span className="text-[11px] text-muted-foreground">
                                        {p.free_daily ? `~${p.free_daily.toLocaleString()} req/day free` : (p.auth === "none" ? "unlimited (self-host)" : "paid / no set free cap")}
                                    </span>
                                    <Button size="sm" variant="outline" onClick={() => openAdd(p.id)} data-testid={`ai-add-${p.id}`}>
                                        <Plus className="w-3.5 h-3.5 mr-1" /> Add account
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Accounts */}
            <Card>
                <CardHeader className="pb-2 flex-row items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" /> Accounts</CardTitle>
                    <span className="text-xs text-muted-foreground">{accounts.length} configured</span>
                </CardHeader>
                <CardContent>
                    {accounts.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-6 text-center">No accounts yet — add one from a provider above.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="ai-accounts-table">
                                <thead>
                                    <tr className="text-left text-xs text-muted-foreground border-b border-border">
                                        <th className="py-2 pr-3">Account</th>
                                        <th className="py-2 pr-3">Key</th>
                                        <th className="py-2 pr-3">Privacy</th>
                                        <th className="py-2 pr-3">Priority</th>
                                        <th className="py-2 pr-3">Usage today</th>
                                        <th className="py-2 pr-3">Enabled</th>
                                        <th className="py-2 pr-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accounts.map((a) => (
                                        <tr key={a.id} className="border-b border-border/60">
                                            <td className="py-2 pr-3">
                                                <div className="font-medium">{a.label}</div>
                                                <div className="text-[11px] text-muted-foreground">{a.provider}{a.verified && <span className="ml-1 text-emerald-600">· verified</span>}</div>
                                            </td>
                                            <td className="py-2 pr-3 tabular text-xs">{a.has_key ? `••••${a.api_key_last4}` : (providerMeta[a.provider]?.auth === "none" ? "n/a" : "—")}</td>
                                            <td className="py-2 pr-3"><PrivacyBadge tier={a.privacy_tier} /></td>
                                            <td className="py-2 pr-3">
                                                <Input type="number" value={a.priority} className="h-8 w-16"
                                                    onChange={(e) => setAccounts(accounts.map((x) => x.id === a.id ? { ...x, priority: e.target.value } : x))}
                                                    onBlur={(e) => patchAccount(a.id, { priority: Number(e.target.value) })} />
                                            </td>
                                            <td className="py-2 pr-3 w-40">
                                                {a.free_daily ? (
                                                    <div>
                                                        <div className="flex justify-between text-[11px] text-muted-foreground mb-0.5">
                                                            <span>{a.used_today}/{a.free_daily}</span>
                                                            <span>{a.pct_today ?? 0}%</span>
                                                        </div>
                                                        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                                                            <div className={`h-full ${a.at_limit ? "bg-destructive" : "bg-primary"}`} style={{ width: `${Math.min(100, a.pct_today || 0)}%` }} />
                                                        </div>
                                                    </div>
                                                ) : <span className="text-[11px] text-muted-foreground">{a.used_today} · no cap</span>}
                                            </td>
                                            <td className="py-2 pr-3">
                                                <Switch checked={a.enabled} onCheckedChange={(v) => patchAccount(a.id, { enabled: v })} />
                                            </td>
                                            <td className="py-2 pr-3">
                                                <div className="flex items-center gap-1 justify-end">
                                                    <Button size="sm" variant="ghost" onClick={() => verify(a.id)} title="Verify"><CheckCircle2 className="w-4 h-4" /></Button>
                                                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeAccount(a.id)} title="Remove"><Trash2 className="w-4 h-4" /></Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Routing + failover */}
            <Card>
                <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4" /> Routing &amp; failover</CardTitle>
                    <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground">Task</Label>
                        <Select value={task} onValueChange={(v) => { setTask(v); setResolution(null); }}>
                            <SelectTrigger className="h-8 w-40" data-testid="ai-task-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {tasks.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                        Order accounts by preference. The first enabled account that is under its free daily limit is tried,
                        followed by the remaining routes. If none are available, AI features may return static fallback responses.
                    </p>
                    <div className="space-y-2" data-testid="ai-routing-list">
                        {items.length === 0 && <div className="text-sm text-muted-foreground">No routes for this task yet.</div>}
                        {items.map((it, i) => (
                            <div key={i} className="flex items-center gap-2 flex-wrap rounded-lg border border-border p-2">
                                <span className="text-xs w-6 text-center text-muted-foreground">#{i + 1}</span>
                                <Select value={it.account_id} onValueChange={(v) => updateItem(i, { account_id: v, model: "" })}>
                                    <SelectTrigger className="h-8 w-56"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{accLabel(a.id)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Select value={it.model || ""} onValueChange={(v) => updateItem(i, { model: v })}>
                                    <SelectTrigger className="h-8 w-56"><SelectValue placeholder="model (cheapest first)" /></SelectTrigger>
                                    <SelectContent>
                                        {accModels(it.account_id).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <div className="flex items-center gap-1 ml-auto">
                                    <Button size="sm" variant="ghost" onClick={() => move(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => move(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
                                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeItem(i)}><Trash2 className="w-4 h-4" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={addRouteItem} data-testid="ai-route-add"><Plus className="w-4 h-4 mr-1" /> Add step</Button>
                        <Button size="sm" onClick={saveRoutes} data-testid="ai-route-save">Save routing</Button>
                        <div className="ml-auto flex gap-2">
                            <Button size="sm" variant="outline" onClick={preview} data-testid="ai-route-preview">Preview resolution</Button>
                            <Button size="sm" variant="outline" onClick={simulate} data-testid="ai-route-simulate"><Zap className="w-4 h-4 mr-1" /> Simulate call</Button>
                        </div>
                    </div>

                    {resolution && (
                        <div className="rounded-lg border border-border p-3 bg-secondary/30" data-testid="ai-resolution">
                            <div className="text-sm mb-2">
                                {resolution.chosen ? (
                                    <span className="text-emerald-600 font-medium">→ Using {resolution.chosen.label} · {resolution.chosen.model || "(default model)"}</span>
                                ) : (
                                    <span className="text-amber-600 font-medium">→ No routed account available — AI features may use a static fallback response</span>
                                )}
                            </div>
                            <div className="space-y-1">
                                {resolution.trace.map((t, i) => (
                                    <div key={i} className="text-xs flex items-center gap-2">
                                        <span className={t.skipped ? "text-muted-foreground line-through" : "text-foreground"}>
                                            {t.label || t.account_id} · {t.model || "(model)"}
                                        </span>
                                        {t.skipped && <Badge variant="outline" className="text-[10px]">{t.reason}</Badge>}
                                        {!t.skipped && <Badge variant="outline" className="text-[10px] bg-emerald-500/12 text-emerald-600 border-emerald-500/30">selected</Badge>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Add account dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add {providerMeta[form.provider]?.name || form.provider} account</DialogTitle>
                        <DialogDescription>{providerMeta[form.provider]?.note}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Label</Label>
                            <Input value={form.label} placeholder={`e.g. ${providerMeta[form.provider]?.name || ""} (primary)`} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="ai-form-label" />
                        </div>
                        {providerMeta[form.provider]?.auth !== "none" && providerMeta[form.provider]?.auth !== "managed" && (
                            <div>
                                <Label>API key</Label>
                                <Input type="password" value={form.api_key} placeholder="sk-… / gsk_… (stored server-side, shown masked)" onChange={(e) => setForm({ ...form, api_key: e.target.value })} data-testid="ai-form-key" />
                            </div>
                        )}
                        {(providerMeta[form.provider]?.needs_base_url || providerMeta[form.provider]?.auth === "none") && (
                            <div>
                                <Label>Base URL</Label>
                                <Input value={form.base_url} placeholder="http://host:11434 or gateway URL" onChange={(e) => setForm({ ...form, base_url: e.target.value })} data-testid="ai-form-baseurl" />
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Priority (lower = first)</Label>
                                <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="ai-form-priority" />
                            </div>
                            <div>
                                <Label>Free daily limit</Label>
                                <Input type="number" value={form.free_daily} onChange={(e) => setForm({ ...form, free_daily: e.target.value })} data-testid="ai-form-freedaily" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                        <Button onClick={createAccount} data-testid="ai-form-save">Add account</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
